import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Store, Trash2, Plus, Save, BarChart3, CalendarX, Leaf, UtensilsCrossed, TrendingUp, DollarSign, ClipboardList, Clock, Star } from "lucide-react";
import { format, parseISO } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

import { CHART_COLORS, DIETARY_OPTIONS } from "../lib/vendorConstants";

/**
 * The vendor's own portal — dashboard, profile/menu editing, and availability.
 *
 * This is a separate screen from the staff-facing vendor directory even though
 * both live at /vendors. A user with the `vendor` role never sees the
 * directory, and nobody else ever sees this. Keeping them apart means neither
 * has to carry the other's state.
 */
export default function VendorPortal() {
  const { authAxios, isVendor } = useAuth();
  const [loading, setLoading] = useState(true);

  // Vendor self-service state
  const [myVendor, setMyVendor] = useState(null);
  const [myVendorForm, setMyVendorForm] = useState(null);
  const [savingMyVendor, setSavingMyVendor] = useState(false);
  const [myMenuItemForm, setMyMenuItemForm] = useState({ name: "", price: "", category: "" });
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [vendorDashboard, setVendorDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [vendorTab, setVendorTab] = useState("dashboard");

  // Fetch vendor's own profile if they're a vendor
  const fetchMyVendor = useCallback(async () => {
    if (!isVendor) return;
    try {
      const api = authAxios();
      const res = await api.get("/my-vendor");
      setMyVendor(res.data);
      setMyVendorForm({
        name: res.data.name || "",
        description: res.data.description || "",
        categories: res.data.categories || [],
        contact_email: res.data.contact_email || "",
        contact_phone: res.data.contact_phone || "",
        address: res.data.address || "",
        menu_items: res.data.menu_items || [],
        dietary_tags: res.data.dietary_tags || [],
      });
      // Load unavailable dates
      const dates = (res.data.unavailable_dates || []).map((d) => {
        try { return parseISO(d); } catch { return null; }
      }).filter(Boolean);
      setUnavailableDates(dates);
    } catch {
      setMyVendor(null);
      setMyVendorForm(null);
    } finally {
      setLoading(false);
    }
  }, [authAxios, isVendor]);

  const fetchVendorDashboard = useCallback(async () => {
    if (!isVendor) return;
    setDashboardLoading(true);
    try {
      const api = authAxios();
      const res = await api.get("/my-vendor/dashboard");
      setVendorDashboard(res.data);
    } catch {
      setVendorDashboard(null);
    } finally {
      setDashboardLoading(false);
    }
  }, [authAxios, isVendor]);

  const handleSaveMyVendor = async () => {
    if (!myVendor || !myVendorForm) return;
    setSavingMyVendor(true);
    try {
      const api = authAxios();
      const res = await api.put(`/vendors/${myVendor.vendor_id}`, myVendorForm);
      setMyVendor(res.data);
      toast.success("Profile updated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingMyVendor(false);
    }
  };

  const addMyMenuItem = () => {
    if (!myMenuItemForm.name || !myVendorForm) return;
    setMyVendorForm({
      ...myVendorForm,
      menu_items: [...myVendorForm.menu_items, { ...myMenuItemForm, price: parseFloat(myMenuItemForm.price) || 0 }]
    });
    setMyMenuItemForm({ name: "", price: "", category: "" });
  };

  const removeMyMenuItem = (idx) => {
    setMyVendorForm({
      ...myVendorForm,
      menu_items: myVendorForm.menu_items.filter((_, i) => i !== idx)
    });
  };

  const toggleMyDietaryTag = (tag) => {
    if (!myVendorForm) return;
    setMyVendorForm({
      ...myVendorForm,
      dietary_tags: myVendorForm.dietary_tags.includes(tag)
        ? myVendorForm.dietary_tags.filter((t) => t !== tag)
        : [...myVendorForm.dietary_tags, tag]
    });
  };

  const handleSaveAvailability = async () => {
    if (!myVendor) return;
    setSavingAvailability(true);
    try {
      const api = authAxios();
      const dateStrings = unavailableDates.map((d) => format(d, "yyyy-MM-dd"));
      await api.put(`/vendors/${myVendor.vendor_id}/availability`, { unavailable_dates: dateStrings });
      toast.success("Availability updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update availability");
    } finally {
      setSavingAvailability(false);
    }
  };

  useEffect(() => {
    fetchMyVendor();
    fetchVendorDashboard();
  }, [fetchMyVendor, fetchVendorDashboard]);

  if (loading) return <div className="p-6 md:p-8 lg:p-12 w-full"><div className="h-40 bg-muted rounded-xl animate-pulse" /></div>;
  if (!myVendor || !myVendorForm) {
    return (
      <div className="p-6 md:p-8 lg:p-12 w-full text-center py-16">
        <Store className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
        <h2 className="font-heading text-2xl font-bold text-foreground">No Vendor Profile Linked</h2>
        <p className="text-muted-foreground mt-2">Contact an administrator to link your account to a vendor profile.</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-6" data-testid="my-vendor-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-3">
          <Store className="h-8 w-8 text-primary" />
          {vendorDashboard?.vendor_name || myVendor.name || "My Vendor"}
        </h1>
        <p className="text-muted-foreground mt-1">Manage your dashboard, profile, and availability</p>
      </div>

      <Tabs value={vendorTab} onValueChange={setVendorTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md" data-testid="vendor-tabs">
          <TabsTrigger value="dashboard" data-testid="vendor-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="profile" data-testid="vendor-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="availability" data-testid="vendor-tab-availability">Availability</TabsTrigger>
        </TabsList>

        {/* ── DASHBOARD TAB ── */}
        <TabsContent value="dashboard" className="space-y-6 mt-6" data-testid="vendor-dashboard-tab">
          {dashboardLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />)}
            </div>
          ) : vendorDashboard ? (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="vendor-stats-cards">
                <Card className="border-border/50 shadow-sm">
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    <div className="h-10 w-10 rounded-full bg-indigo-500/10 flex items-center justify-center mb-2">
                      <ClipboardList className="h-5 w-5 text-indigo-500" />
                    </div>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-total-events">{vendorDashboard.total_events}</p>
                    <p className="text-xs text-muted-foreground">Events Assigned</p>
                  </CardContent>
                </Card>
                <Card className="border-border/50 shadow-sm">
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center mb-2">
                      <UtensilsCrossed className="h-5 w-5 text-amber-500" />
                    </div>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-total-meals">{vendorDashboard.total_meals_assigned}</p>
                    <p className="text-xs text-muted-foreground">Meals Served</p>
                  </CardContent>
                </Card>
                <Card className="border-border/50 shadow-sm">
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    <div className="h-10 w-10 rounded-full bg-yellow-500/10 flex items-center justify-center mb-2">
                      <Star className="h-5 w-5 text-yellow-500" />
                    </div>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-avg-rating">
                      {vendorDashboard.avg_rating > 0 ? vendorDashboard.avg_rating : "—"}
                    </p>
                    <p className="text-xs text-muted-foreground">{vendorDashboard.total_reviews} Review{vendorDashboard.total_reviews !== 1 ? "s" : ""}</p>
                  </CardContent>
                </Card>
                <Card className="border-border/50 shadow-sm">
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center mb-2">
                      <DollarSign className="h-5 w-5 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-revenue">${vendorDashboard.total_revenue.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total Revenue</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Activity */}
                <Card className="border-border/50 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-indigo-500" /> Monthly Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {vendorDashboard.monthly_data.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={vendorDashboard.monthly_data}>
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} name="Meals" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
                        No activity data yet. Meals assigned to you will appear here.
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Meal Type Distribution */}
                <Card className="border-border/50 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-amber-500" /> Meal Types
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {vendorDashboard.meal_type_data.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={vendorDashboard.meal_type_data} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={70} label={({ type, count }) => `${type} (${count})`}>
                            {vendorDashboard.meal_type_data.map((_, idx) => (
                              <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
                        No meal type data yet.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Upcoming Events & Recent Reviews */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upcoming Events */}
                <Card className="border-border/50 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-500" /> Upcoming Events
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {vendorDashboard.upcoming_events.length > 0 ? (
                      <div className="space-y-3" data-testid="vendor-upcoming-events">
                        {vendorDashboard.upcoming_events.map((evt) => (
                          <div key={evt.event_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border/50">
                            <div>
                              <p className="text-sm font-medium text-foreground">{evt.title}</p>
                              <p className="text-xs text-muted-foreground">{evt.location || "No location"}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-medium text-foreground">{evt.start_date}</p>
                              <Badge variant="outline" className="text-[10px] mt-0.5">{evt.status}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-6">No upcoming events. You'll see events here when assigned.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Reviews */}
                <Card className="border-border/50 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-500" /> Recent Reviews
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {vendorDashboard.recent_reviews.length > 0 ? (
                      <div className="space-y-3" data-testid="vendor-recent-reviews">
                        {vendorDashboard.recent_reviews.map((rev, i) => (
                          <div key={i} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex">
                                {[1,2,3,4,5].map((s) => (
                                  <Star key={s} className={`h-3 w-3 ${s <= rev.rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`} />
                                ))}
                              </div>
                              <span className="text-xs text-muted-foreground">{rev.reviewer_name || "Anonymous"}</span>
                            </div>
                            {rev.comment && <p className="text-sm text-foreground">{rev.comment}</p>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-6">No reviews yet.</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Quick Info Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {vendorDashboard.dietary_tags.length > 0 && (
                  <Card className="border-border/50 shadow-sm">
                    <CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Your Dietary Tags</p>
                      <div className="flex flex-wrap gap-1.5">
                        {vendorDashboard.dietary_tags.map((t) => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {vendorDashboard.unavailable_dates.length > 0 && (
                  <Card className="border-border/50 shadow-sm">
                    <CardContent className="p-4">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Upcoming Unavailable Dates</p>
                      <div className="flex flex-wrap gap-1.5">
                        {vendorDashboard.unavailable_dates.slice(0, 5).map((d) => (
                          <Badge key={d} variant="outline" className="text-xs text-red-600 border-red-300">{d}</Badge>
                        ))}
                        {vendorDashboard.unavailable_dates.length > 5 && (
                          <Badge variant="outline" className="text-xs">+{vendorDashboard.unavailable_dates.length - 5} more</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">Dashboard data unavailable</p>
            </div>
          )}
        </TabsContent>

        {/* ── PROFILE TAB ── */}
        <TabsContent value="profile" className="space-y-6 mt-6" data-testid="vendor-profile-tab">
          <Card className="border-border/50 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Business Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name</Label>
                  <Input value={myVendorForm.name} onChange={(e) => setMyVendorForm({ ...myVendorForm, name: e.target.value })} data-testid="my-vendor-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={myVendorForm.contact_email} onChange={(e) => setMyVendorForm({ ...myVendorForm, contact_email: e.target.value })} data-testid="my-vendor-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={myVendorForm.contact_phone} onChange={(e) => setMyVendorForm({ ...myVendorForm, contact_phone: e.target.value })} data-testid="my-vendor-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input value={myVendorForm.address} onChange={(e) => setMyVendorForm({ ...myVendorForm, address: e.target.value })} data-testid="my-vendor-address-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={myVendorForm.description} onChange={(e) => setMyVendorForm({ ...myVendorForm, description: e.target.value })} rows={3} data-testid="my-vendor-description-input" />
              </div>
            </CardContent>
          </Card>

          {/* Dietary Tags */}
          <Card className="border-border/50 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Leaf className="h-4 w-4 text-green-500" /> Dietary Tags
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2" data-testid="my-vendor-dietary-tags">
                {DIETARY_OPTIONS.map((tag) => (
                  <Badge
                    key={tag}
                    variant={myVendorForm.dietary_tags.includes(tag) ? "default" : "outline"}
                    className="cursor-pointer transition-all"
                    onClick={() => toggleMyDietaryTag(tag)}
                    data-testid={`my-vendor-dietary-${tag.toLowerCase()}`}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Menu Items */}
          <Card className="border-border/50 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <UtensilsCrossed className="h-4 w-4 text-orange-500" /> Menu Items
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Item Name</Label>
                  <Input placeholder="Item name" value={myMenuItemForm.name} onChange={(e) => setMyMenuItemForm({ ...myMenuItemForm, name: e.target.value })} data-testid="my-vendor-menu-name" />
                </div>
                <div className="w-28 space-y-1">
                  <Label className="text-xs">Price ($)</Label>
                  <Input type="number" placeholder="0.00" value={myMenuItemForm.price} onChange={(e) => setMyMenuItemForm({ ...myMenuItemForm, price: e.target.value })} data-testid="my-vendor-menu-price" />
                </div>
                <div className="w-32 space-y-1">
                  <Label className="text-xs">Category</Label>
                  <Input placeholder="Category" value={myMenuItemForm.category} onChange={(e) => setMyMenuItemForm({ ...myMenuItemForm, category: e.target.value })} data-testid="my-vendor-menu-category" />
                </div>
                <Button type="button" size="sm" onClick={addMyMenuItem} data-testid="my-vendor-add-menu-item-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {myVendorForm.menu_items.length > 0 && (
                <div className="border rounded-lg divide-y divide-border" data-testid="my-vendor-menu-list">
                  {myVendorForm.menu_items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{item.name}</span>
                        {item.category && <Badge variant="outline" className="text-xs">{item.category}</Badge>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">${Number(item.price || 0).toFixed(2)}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeMyMenuItem(idx)} data-testid={`my-vendor-remove-menu-${idx}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {myVendorForm.menu_items.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No menu items yet. Add your first item above.</p>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveMyVendor} disabled={savingMyVendor} className="px-8" data-testid="save-my-vendor-btn">
              {savingMyVendor ? "Saving..." : "Save Profile"}
            </Button>
          </div>
        </TabsContent>

        {/* ── AVAILABILITY TAB ── */}
        <TabsContent value="availability" className="space-y-6 mt-6" data-testid="vendor-availability-tab">
          <Card className="border-border/50 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CalendarX className="h-4 w-4 text-red-500" /> Unavailable Dates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Click dates on the calendar to mark days you are unavailable. Click again to remove.</p>
              <div className="flex justify-center" data-testid="availability-calendar">
                <Calendar
                  mode="multiple"
                  selected={unavailableDates}
                  onSelect={(dates) => setUnavailableDates(dates || [])}
                  className="rounded-lg border"
                  modifiers={{ unavailable: unavailableDates }}
                  modifiersClassNames={{ unavailable: "bg-red-500/20 text-red-600 font-bold" }}
                />
              </div>
              {unavailableDates.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{unavailableDates.length} date{unavailableDates.length !== 1 ? "s" : ""} marked unavailable</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="unavailable-dates-list">
                    {unavailableDates.sort((a, b) => a - b).map((d, i) => (
                      <Badge key={i} variant="outline" className="text-xs text-red-600 border-red-300">
                        {format(d, "MMM d, yyyy")}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={handleSaveAvailability} disabled={savingAvailability} data-testid="save-availability-btn">
                  {savingAvailability ? "Saving..." : "Save Availability"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
